/**
 * useChatExpiryReminder Hook
 *
 * Once per day, audits all joined rooms for inactivity approaching the server's
 * inactive-room deletion threshold (fetched at runtime from
 * serverConfigService; falls back to 180 days). Rooms where last activity is
 * older than (deletionDays - chatExpiryReminderDays) days ago are surfaced in
 * a modal.
 *
 * Settings:
 *   settings.chatExpiryReminderDays - days before expiry to start warning (0 = disabled, default = 7)
 *
 * The test button in Settings dispatches a 'ti-m:trigger-expiry-check' window event,
 * which bypasses the once-per-day guard.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { ClientEvent, SyncState } from 'matrix-js-sdk';
import { useSettings } from '../context/SettingsContext';
import matrixService from '../services/matrixService';
import {
  initInactiveDeletionConfig,
  getInactiveDeletionDays,
} from '../services/serverConfigService';
import { isServerNoticesRoom } from '../utils/serverNoticesUtils';
import { MS_PER_DAY } from '../utils/timeUtils';
import logger from '../utils/logger';

const LAST_CHECK_KEY = 'ti_m_expiry_reminder_last_check';
const SYNC_READY_FALLBACK_MS = 30000;

const isPreparedState = (s) => (
  s === (SyncState && SyncState.Prepared) ||
  s === (SyncState && SyncState.Syncing) ||
  s === 'PREPARED' ||
  s === 'SYNCING'
);

// Conversation event types that count as "activity" (mirrors useRoomList logic)
const ACTIVITY_EVENT_TYPES = new Set([
  'm.room.message',
  'm.room.encrypted',
  'm.sticker',
  'm.reaction',
  'm.call.invite',
  'm.call.answer',
  'm.call.hangup',
  'm.room.member',
  'm.room.redaction',
]);

/**
 * Get the last meaningful activity timestamp for a room.
 * Mirrors the filtering logic in useRoomList.js getLastActivity().
 * @param {Room} room - Matrix room object
 * @param {string} myUserId - Current user Matrix ID
 * @returns {number} Timestamp in ms, or 0 if not determinable
 */
const getRoomLastActivity = (room, _myUserId) => {
  if (!room?.currentState) return 0;
  if (room.getMyMembership() !== 'join') return 0;

  const timeline = room.getLiveTimeline();
  if (!timeline) {
    const createEvent = room.currentState.getStateEvents('m.room.create', '');
    return createEvent?.getTs() || 0;
  }

  const events = timeline.getEvents();

  // Filter out profile-only member event changes
  const filteredEvents = events.filter(event => {
    const type = event.getType();
    if (type === 'm.room.member') {
      const content = event.getContent();
      const prevContent = event.getPrevContent();
      if (content.membership !== prevContent?.membership) return true;
      const avatarChanged = content.avatar_url !== prevContent?.avatar_url;
      const displaynameChanged = content.displayname !== prevContent?.displayname;
      if (avatarChanged || displaynameChanged) return false;
    }
    return true;
  });

  // Walk backwards to find the most recent meaningful event
  for (let i = filteredEvents.length - 1; i >= 0; i--) {
    const event = filteredEvents[i];
    if (ACTIVITY_EVENT_TYPES.has(event.getType())) {
      const ts = event.getTs();
      if (ts && typeof ts === 'number' && !Number.isNaN(ts)) return ts;
    }
  }

  // Fallback to room creation
  const createEvent = room.currentState.getStateEvents('m.room.create', '');
  return createEvent?.getTs() || 0;
};

export const useChatExpiryReminder = () => {
  const { settings } = useSettings();
  const [expiringRooms, setExpiringRooms] = useState([]);
  const [showModal, setShowModal] = useState(false);

  const runCheck = useCallback((force = false) => {
    const reminderDays = settings?.chatExpiryReminderDays ?? 7;

    if (reminderDays <= 0) {
      logger.debug('[ChatExpiryReminder] Reminders disabled (chatExpiryReminderDays=0)');
      return;
    }

    // Skip if already ran today (unless forced via test button)
    if (!force) {
      try {
        const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
        if (lastCheck) {
          const lastDate = new Date(lastCheck).toDateString();
          const today = new Date().toDateString();
          if (lastDate === today) {
            logger.debug('[ChatExpiryReminder] Already checked today, skipping');
            return;
          }
        }
      } catch {
        // localStorage may be unavailable in some environments
      }
    }

    const client = matrixService.getClient();
    if (!client) {
      logger.debug('[ChatExpiryReminder] Client not ready, skipping expiry check');
      return;
    }

    // Don't poison the once-per-day gate by running before initial /sync has
    // populated room timelines. The test button (force=true) bypasses this so
    // diagnostic checks remain possible regardless of sync state.
    const syncReady = typeof client.getSyncState !== 'function'
      || isPreparedState(client.getSyncState());
    if (!syncReady && !force) {
      logger.debug('[ChatExpiryReminder] Sync not prepared yet, deferring check');
      return;
    }

    const myUserId = client.getUserId();
    const rooms = matrixService.getRooms();
    const now = Date.now();

    // Use the server-configured deletion threshold; defaults to 180 days when
    // the Synapse module is absent or its config endpoint failed.
    const deletionDays = getInactiveDeletionDays();

    // Defensive clamp: if the server's deletion window is shorter than the
    // user's saved reminder window, every room would otherwise appear to be
    // expiring forever. Cap reminderDays at deletionDays-1, floored at 0.
    const effectiveReminderDays = Math.min(
      reminderDays,
      Math.max(0, deletionDays - 1),
    );
    const warnThresholdMs = (deletionDays - effectiveReminderDays) * MS_PER_DAY;

    const expiring = [];
    for (const room of rooms) {
      if (room.getMyMembership() !== 'join') continue;
      if (isServerNoticesRoom(room, client)) continue;

      const lastActivity = getRoomLastActivity(room, myUserId);
      if (!lastActivity) continue;

      const inactiveMs = now - lastActivity;
      if (inactiveMs >= warnThresholdMs) {
        const daysInactive = Math.floor(inactiveMs / MS_PER_DAY);
        const daysRemaining = Math.max(0, deletionDays - daysInactive);
        expiring.push({
          id: room.roomId,
          name: room.name || room.roomId,
          lastActivity,
          daysInactive,
          daysRemaining,
        });
      }
    }

    // Sort by daysRemaining ascending (most urgent first)
    expiring.sort((a, b) => a.daysRemaining - b.daysRemaining);

    logger.debug(`[ChatExpiryReminder] Found ${expiring.length} expiring rooms out of ${rooms.length} total`);

    // Stamp the once-per-day gate only on real scheduled runs. Force calls
    // (test button) skip the write so they don't burn the daily quota.
    if (!force) {
      try {
        localStorage.setItem(LAST_CHECK_KEY, new Date().toISOString());
      } catch {
        // Ignore
      }
    }

    if (expiring.length > 0) {
      setExpiringRooms(expiring);
      setShowModal(true);
    } else if (force) {
      window.dispatchEvent(new CustomEvent('ti-m:expiry-check-empty'));
    }
  }, [settings?.chatExpiryReminderDays]);

  // Read latest runCheck via a ref so the scheduling effect's identity is
  // stable across settings changes (its previous [runCheck] dep tore down the
  // pending timer when account-data sync replaced default settings).
  const runCheckRef = useRef(runCheck);
  useEffect(() => { runCheckRef.current = runCheck; }, [runCheck]);

  // Schedule the initial check, gated on Matrix sync reaching PREPARED.
  useEffect(() => {
    let cancelled = false;
    let fallbackTimer = null;
    let syncListener = null;
    const client = matrixService.getClient();

    if (!client) {
      logger.debug('[ChatExpiryReminder] No client at mount; relying on test-button trigger');
      return undefined;
    }

    const runIfNotCancelled = async () => {
      if (cancelled) return;
      try { await initInactiveDeletionConfig(); } catch { /* service already returns fallback */ }
      if (cancelled) return;
      runCheckRef.current(false);
    };

    const syncReadyImmediate = typeof client.getSyncState !== 'function'
      || isPreparedState(client.getSyncState());

    if (syncReadyImmediate) {
      runIfNotCancelled();
      return () => { cancelled = true; };
    }

    syncListener = (state) => {
      if (cancelled) return;
      if (isPreparedState(state)) {
        if (syncListener) {
          try { client.off(ClientEvent.Sync, syncListener); } catch { /* ignore */ }
          syncListener = null;
        }
        if (fallbackTimer) { clearTimeout(fallbackTimer); fallbackTimer = null; }
        runIfNotCancelled();
      }
    };
    client.on(ClientEvent.Sync, syncListener);

    fallbackTimer = setTimeout(() => {
      if (cancelled) return;
      if (syncListener) {
        try { client.off(ClientEvent.Sync, syncListener); } catch { /* ignore */ }
        syncListener = null;
      }
      logger.warn('[ChatExpiryReminder] Sync-ready fallback elapsed; skipping initial check');
    }, SYNC_READY_FALLBACK_MS);

    return () => {
      cancelled = true;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (syncListener) {
        try { client.off(ClientEvent.Sync, syncListener); } catch { /* ignore */ }
      }
    };
  }, []);

  // Listen for the test-button event (dispatched from Settings page)
  useEffect(() => {
    let active = true;
    const handler = async () => {
      try { await initInactiveDeletionConfig(); } catch { /* fallback already cached */ }
      if (!active) return;
      runCheckRef.current(true);
    };
    window.addEventListener('ti-m:trigger-expiry-check', handler);
    return () => {
      active = false;
      window.removeEventListener('ti-m:trigger-expiry-check', handler);
    };
  }, []);

  const dismiss = useCallback(() => {
    setShowModal(false);
    setExpiringRooms([]);
  }, []);

  // Remove a single room from the list (after leaving)
  const removeRoom = useCallback((roomId) => {
    setExpiringRooms(prev => {
      const updated = prev.filter(r => r.id !== roomId);
      if (updated.length === 0) setShowModal(false);
      return updated;
    });
  }, []);

  return { expiringRooms, showModal, dismiss, removeRoom };
};
